/**
 * SignedPackets — a customer's signed packets from account opening (Bill 2026-10-17: "point them at each customer's
 * signed packet from opening, which ROP does have").
 *
 * One card per opening application: who and what it opened, then the forms they signed (with each signer and date),
 * what they were given to read, and the online application or ID scans on file. Any document opens in place; the
 * whole packet can be printed as the customer copy or downloaded for Aurora. The server streams every file and
 * records each open; no SharePoint link reaches the browser.
 *
 * Used by the customer full record's Documents tab and by the Documents page.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { Archive, BookOpen, Download, FileCheck2, FileText, IdCard, Inbox, Printer, ScrollText } from 'lucide-react';
import type { SignedPacket, SignedPacketDocument } from '@retail-ops/shared-types';
import { PdfPopupViewer } from '@/components/common/PdfPopupViewer';
import { OfficerBadge } from '@/components/common/OfficerBadge';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useOfficerEmailLookup, useOfficerNameLookup } from '@/hooks/useOfficerCodeLookup';
import { useStaffProfiles, profileFor } from '@/hooks/useStaffProfiles';
import './signedPackets.css';

const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
const fmt = (iso?: string | null): string => (iso ? DATE.format(new Date(iso)) : '');

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function kindIcon(kind: string): JSX.Element {
  if (kind === 'signed-document') return <FileCheck2 size={16} aria-hidden />;
  if (kind === 'presented-disclosure') return <BookOpen size={16} aria-hidden />;
  if (kind === 'jotform-application') return <Inbox size={16} aria-hidden />;
  if (/id[-_]?scan/i.test(kind)) return <IdCard size={16} aria-hidden />;
  return <FileText size={16} aria-hidden />;
}

function docDetail(d: SignedPacketDocument): string {
  if (d.kind === 'signed-document') {
    const who = d.signers.map((s) => s.name).filter(Boolean);
    const when = fmt(d.at ?? d.signers.at(-1)?.signedAt);
    return [who.length ? `Signed by ${who.join(', ')}` : 'Signed', when].filter(Boolean).join(' · ');
  }
  if (d.kind === 'presented-disclosure') return `Given to read${d.at ? ` · ${fmt(d.at)}` : ''}`;
  return d.at ? `On file · ${fmt(d.at)}` : 'On file';
}

const SECTIONS: Array<{ title: string; test: (d: SignedPacketDocument) => boolean }> = [
  { title: 'Signed', test: (d) => d.kind === 'signed-document' },
  { title: 'Given to read', test: (d) => d.kind === 'presented-disclosure' },
  { title: 'Also on file', test: (d) => d.kind !== 'signed-document' && d.kind !== 'presented-disclosure' },
];

interface Viewing { title: string; load: () => Promise<Blob> }

function PacketCard({ p, onView, showCustomerLink }: { p: SignedPacket; onView: (v: Viewing) => void; showCustomerLink: boolean }): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const { config } = useConfig();
  const emailFor = useOfficerEmailLookup();
  const officerName = useOfficerNameLookup();
  const upn = p.officerCode ? emailFor(p.officerCode) : null;
  const profiles = useStaffProfiles([upn ?? null]);
  const [busy, setBusy] = useState<'aurora' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const productName = (code: string): string => config?.products?.find((x) => x.code === code)?.name ?? code;
  const accounts = p.accounts.map((a) => `${productName(a.productCode)} ··${a.acctId.slice(-4)}`).join(' · ');
  const signedCount = p.documents.filter((d) => d.kind === 'signed-document').length;

  const aurora = async (): Promise<void> => {
    setBusy('aurora');
    setError(null);
    try { saveBlob(await client.retail.carts.auroraPackage(p.cartId), `aurora-${p.cartId.slice(0, 8)}.zip`); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not build the Aurora package.'); }
    finally { setBusy(null); }
  };

  return (
    <article className="sp-card" aria-label={`Opening application for ${p.customer}`}>
      <header className="sp-card__head">
        {p.officerCode && (
          <OfficerBadge code={p.officerCode} upn={upn} profile={profileFor(profiles, upn)} fallbackName={officerName(p.officerCode, null) || p.officerCode}
            reference={{ title: p.customer, cartId: p.cartId }} />
        )}
        <div className="sp-card__who">
          {showCustomerLink && p.primaryCif ? (
            <button type="button" className="sp-card__name sp-card__name--link" onClick={() => navigate(`/rdl/cif/${encodeURIComponent(p.primaryCif!)}`)}
              title="See every packet for this customer">
              {p.customer}
            </button>
          ) : (
            <span className="sp-card__name">{p.customer}</span>
          )}
          <span className="sp-card__sub">{accounts || 'Account opening'}</span>
          <span className="sp-card__meta">
            {p.committedAt ? `Opened ${fmt(p.committedAt)}` : `Started ${fmt(p.openedAt)} · not finished`}
            {` · ${signedCount} signed`}
          </span>
        </div>
        <div className="sp-card__actions">
          <button type="button" className="sp-btn" onClick={() => onView({ title: `Customer copy · ${p.customer}`, load: () => client.retail.carts.customerCopy(p.cartId) })}
            title="Everything the customer signed and was given, as one PDF to print or mail">
            <Printer size={14} aria-hidden /> Customer copy
          </button>
          <button type="button" className="sp-btn" onClick={() => void aurora()} disabled={busy !== null}
            title="The filing package for Aurora (zip with every document, a manifest and the certificate)">
            {busy === 'aurora' ? <Archive size={14} aria-hidden /> : <Download size={14} aria-hidden />} {busy === 'aurora' ? 'Building…' : 'Aurora package'}
          </button>
        </div>
      </header>
      {error && <div className="sp-card__error" role="alert">{error}</div>}
      {SECTIONS.map((s) => {
        const docs = p.documents.filter(s.test);
        if (docs.length === 0) return null;
        return (
          <section key={s.title} className="sp-sec">
            <h4 className="sp-sec__title">{s.title} <span className="sp-sec__n">{docs.length}</span></h4>
            <ul className="sp-docs">
              {docs.map((d) => (
                <li key={d.ref}>
                  <button type="button" className={`sp-doc sp-doc--${d.kind === 'signed-document' ? 'signed' : d.kind === 'presented-disclosure' ? 'read' : 'file'}`}
                    onClick={() => onView({ title: `${d.title} · ${p.customer}`, load: () => client.retail.carts.signedDocumentBlob(p.cartId, d.ref) })}
                    aria-label={`Open ${d.title}. ${docDetail(d)}`}>
                    <span className="sp-doc__icon">{kindIcon(d.kind)}</span>
                    <span className="sp-doc__text">
                      <span className="sp-doc__title">{d.title}</span>
                      <span className="sp-doc__detail">{docDetail(d)}</span>
                    </span>
                    <span className="sp-doc__open">Open</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </article>
  );
}

export function SignedPackets({ packets, loading, error, emptyText, showCustomerLink = false }: {
  packets: SignedPacket[] | null;
  loading: boolean;
  error: string | null;
  emptyText: string;
  showCustomerLink?: boolean;
}): JSX.Element {
  const [viewing, setViewing] = useState<Viewing | null>(null);
  const close = useCallback(() => setViewing(null), []);
  return (
    <div className="sp">
      {loading ? (
        <div className="sp-state"><ScrollText size={18} aria-hidden /> Loading signed packets…</div>
      ) : error ? (
        <div className="sp-state sp-state--error" role="alert">{error}</div>
      ) : !packets || packets.length === 0 ? (
        <div className="sp-state"><ScrollText size={18} aria-hidden /> {emptyText}</div>
      ) : (
        packets.map((p) => <PacketCard key={p.cartId} p={p} onView={setViewing} showCustomerLink={showCustomerLink} />)
      )}
      <PdfPopupViewer open={viewing !== null} title={viewing?.title ?? 'Document'} load={viewing?.load ?? (() => Promise.reject(new Error('No document')))} onClose={close} />
    </div>
  );
}
